import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { Clock, Users, TrendingUp, MessageSquare, Lock, Globe } from 'lucide-react';

const MarketCard = ({ market, showOrgBadge = false }) => {
  const { t, language } = useLanguage();
  const totalPool = (market?.yes_pool || 0) + (market?.no_pool || 0);
  const yesPercent = totalPool > 0 ? ((market?.yes_pool || 0) / totalPool) * 100 : 50;
  const noPercent = totalPool > 0 ? ((market?.no_pool || 0) / totalPool) * 100 : 50;

  const endDate = market?.end_date ? new Date(market.end_date) : null;
  const now = new Date();
  const isEnded = endDate ? endDate <= now : false;
  const timeLeft = endDate ? getTimeLeft(endDate, t) : '';

  const statusColors = {
    open: 'bg-green-600/20 text-green-400 border-green-600/30',
    closed: 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30',
    resolved: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
  };

  const statusMap = {
    open: 'statusOpen',
    closed: 'statusClosed',
    resolved: 'statusResolved',
    cancelled: 'statusCancelled'
  };

  return (
    <Link to={`/market/${market?.id}`} data-testid={`market-card-${market?.id}`}>
      <Card className="market-card h-full flex flex-col card-hover overflow-hidden">
        {market?.image_url && (
          <div className="w-full h-32 relative overflow-hidden">
            <img 
              src={market.image_url} 
              alt={market.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent" />
          </div>
        )}
        <CardHeader className={cn("pb-3", market?.image_url && "-mt-10 relative z-10")}>
          <div className="flex items-start justify-between gap-2">
            <Badge 
              variant="outline" 
              className={statusColors[market?.status] || ''}
              data-testid={`market-status-${market?.id}`}
            >
              {t(statusMap[market?.status] || market?.status)}
            </Badge>
            <div className="flex gap-1 flex-wrap">
              {showOrgBadge && market?.organization_name && (
                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 px-1.5 max-w-[140px] truncate">
                  {market.organization_name}
                </Badge>
              )}
              {market?.tiered_market_id && (
                <Badge variant="secondary" className="bg-blue-600/10 text-blue-400 border-blue-600/20 px-1.5">
                  {language === 'de' ? 'Staffelung' : 'Tiered'}
                </Badge>
              )}
              {market?.group_id ? (
                <Badge variant="secondary" className="bg-purple-500/10 text-purple-400 border-purple-500/20 px-1.5">
                  <Users className="w-3 h-3 mr-1" />
                  {market.group_name || (language === 'de' ? 'Gruppe' : 'Group')}
                </Badge>
              ) : market?.visibility_type === 'specific_users' ? (
                <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 px-1.5">
                  <Lock className="w-3 h-3 mr-1" />
                  {language === 'de' ? 'Privat' : 'Private'}
                </Badge>
              ) : market?.visibility_type === 'email_domain' ? (
                <Badge variant="secondary" className="bg-blue-500/10 text-blue-400 border-blue-500/20 px-1.5">
                  <Lock className="w-3 h-3 mr-1" />
                  {language === 'de' ? 'Domain' : 'Domain'}
                </Badge>
              ) : (
                <Badge variant="ghost" className="text-muted-foreground/40 px-1.5">
                  <Globe className="w-3 h-3" />
                </Badge>
              )}
              {market?.resolution && (
                <Badge 
                  className={market.resolution === 'yes' ? 'bg-green-600' : 'bg-red-600'}
                  data-testid={`market-resolution-${market.id}`}
                >
                  {market.resolution.toUpperCase()}
                </Badge>
              )}
            </div>
          </div>
          <h3 className="text-lg font-bold leading-tight mt-2 line-clamp-2">
            {market?.title}
          </h3>
        </CardHeader>

        <CardContent className="flex-grow pb-3">
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {market?.description}
          </p>

          {/* Odds Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-green-400 font-mono font-bold">
                YES {yesPercent.toFixed(0)}%
              </span>
              <span className="text-red-400 font-mono font-bold">
                NO {noPercent.toFixed(0)}%
              </span>
            </div>
            <div className="odds-bar">
              <div 
                className="odds-bar-yes" 
                style={{ width: `${yesPercent}%` }}
              />
              <div 
                className="odds-bar-no" 
                style={{ width: `${noPercent}%` }}
              />
            </div>
          </div>
        </CardContent>

        <CardFooter className="pt-3 border-t border-border">
          <div className="flex items-center justify-between w-full text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              <span className="font-mono">{(totalPool || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              <span>{market?.total_bets || 0}</span>
            </div>
            <div className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />
              <span>{market?.comment_count || 0}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span className={isEnded ? 'text-red-400' : ''}>
                {isEnded ? t('ended') : timeLeft}
              </span>
            </div>
          </div>
        </CardFooter>
      </Card>
    </Link>
  );
};

function getTimeLeft(endDate, t) {
  const now = new Date();
  const diff = endDate - now;
  
  if (diff <= 0) return t('ended');
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `${days}${t('days').charAt(0).toLowerCase()} ${hours}${t('hours').charAt(0).toLowerCase()}`;
  if (hours > 0) return `${hours}${t('hours').charAt(0).toLowerCase()} ${minutes}${t('mins').charAt(0).toLowerCase()}`;
  return `${minutes}${t('mins').charAt(0).toLowerCase()}`;
}

export default MarketCard;
